import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, Search, Printer, X, CheckCircle, AlertTriangle, FileText, Package, Truck, Calendar, User, GripVertical } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const DeliveryExchange = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delivery Note 1
  const [deliveryNote1, setDeliveryNote1] = useState(null);
  const [filter1Text, setFilter1Text] = useState('');
  const [showSelectModal1, setShowSelectModal1] = useState(false);
  const [selectedOrdersDN1, setSelectedOrdersDN1] = useState(new Set());
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [draggedOrders, setDraggedOrders] = useState([]); // For multiple orders
  const [dragSource, setDragSource] = useState(null); // 1 or 2
  const [dragOverTarget, setDragOverTarget] = useState(null); // 1 or 2

  // Delivery Note 2
  const [deliveryNote2, setDeliveryNote2] = useState(null);
  const [filter2Text, setFilter2Text] = useState('');
  const [showSelectModal2, setShowSelectModal2] = useState(false);
  const [selectedOrdersDN2, setSelectedOrdersDN2] = useState(new Set());

  // Delivery Notes for selection modal
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load delivery notes for selection modal
  const loadDeliveryNotesForSelection = async () => {
    try {
      setLoadingNotes(true);
      const requestData = {
        Skip: 0,
        Take: 100,
        Status: 'CONFIRMED'
      };
      const response = await MainService.DeliveryNotesInList(requestData);
      if (response && response.Data) {
        setDeliveryNotes(response.Data);
      }
    } catch (err) {
      console.error('Error loading delivery notes:', err);
      setError('Failed to load delivery notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  // Open selection modal
  const openSelectModal = (noteNumber) => {
    setSearchKeyword('');
    loadDeliveryNotesForSelection();
    if (noteNumber === 1) {
      setShowSelectModal1(true);
    } else {
      setShowSelectModal2(true);
    }
  };

  // Select delivery note
  const selectDeliveryNote = async (note, noteNumber) => {
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryNoteById(note.Id);
      if (response) {
        // Check if it's the same as the other delivery note
        if (noteNumber === 1 && deliveryNote2 && deliveryNote2.RefNo === response.RefNo) {
          setError('Select Different Delivery Note');
        } else if (noteNumber === 2 && deliveryNote1 && deliveryNote1.RefNo === response.RefNo) {
          setError('Select Different Delivery Note');
        } else {
          if (noteNumber === 1) {
            setDeliveryNote1(response);
            setFilter1Text('');
            setSelectedOrdersDN1(new Set());
          } else {
            setDeliveryNote2(response);
            setFilter2Text('');
            setSelectedOrdersDN2(new Set());
          }
        }
      }
      if (noteNumber === 1) {
        setShowSelectModal1(false);
      } else {
        setShowSelectModal2(false);
      }
    } catch (err) {
      console.error('Error loading delivery note:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle order selection
  const toggleOrderSelection = (detailId, noteNumber, e) => {
    if (e && (e.ctrlKey || e.metaKey)) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      
      if (noteNumber === 1) {
        setSelectedOrdersDN1(prev => {
          const newSet = new Set(prev);
          if (newSet.has(detailId)) {
            newSet.delete(detailId);
          } else {
            newSet.add(detailId);
          }
          return newSet;
        });
      } else {
        setSelectedOrdersDN2(prev => {
          const newSet = new Set(prev);
          if (newSet.has(detailId)) {
            newSet.delete(detailId);
          } else {
            newSet.add(detailId);
          }
          return newSet;
        });
      }
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, detail, sourceNote) => {
    const selectedSet = sourceNote === 1 ? selectedOrdersDN1 : selectedOrdersDN2;
    const hasSelection = selectedSet.size > 0;
    
    // If there are selected orders, drag all selected ones
    if (hasSelection && selectedSet.has(detail.Id)) {
      const sourceDetails = sourceNote === 1 
        ? getFilteredOrders(deliveryNote1.DeliveryNoteDetails, filter1Text)
        : getFilteredOrders(deliveryNote2.DeliveryNoteDetails, filter2Text);
      
      const selectedDetails = sourceDetails.filter(d => selectedSet.has(d.Id));
      
      if (selectedDetails.length > 0) {
        setDraggedOrders(selectedDetails);
        setDraggedOrder(selectedDetails[0]); // For backward compatibility
        setDragSource(sourceNote);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify(selectedDetails.map(d => d.Id)));
      }
    } else {
      // Single order drag
      setDraggedOrder(detail);
      setDraggedOrders([detail]);
      setDragSource(sourceNote);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', detail.Id);
    }
    
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedOrder(null);
    setDraggedOrders([]);
    setDragSource(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e, targetNote) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragSource && dragSource !== targetNote) {
      setDragOverTarget(targetNote);
    }
  };

  const handleDragLeave = (e) => {
    // Only remove highlight if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTarget(null);
    }
  };

  const handleDrop = async (e, targetNote) => {
    e.preventDefault();
    
    const ordersToMove = draggedOrders.length > 0 ? draggedOrders : (draggedOrder ? [draggedOrder] : []);
    
    if (ordersToMove.length === 0 || !dragSource || dragSource === targetNote) {
      setDragOverTarget(null);
      return;
    }

    const sourceNote = dragSource === 1 ? deliveryNote1 : deliveryNote2;
    const targetDeliveryNote = targetNote === 1 ? deliveryNote1 : deliveryNote2;

    if (!sourceNote || !targetDeliveryNote) {
      setError('Both delivery notes must be selected');
      setDragOverTarget(null);
      return;
    }

    try {
      setLoading(true);
      const exchangeData = {
        Id: sourceNote.Id,
        ExchangeDeliveryNoteId: targetDeliveryNote.Id,
        DeliveryNoteDetails: ordersToMove.map(order => ({ Id: order.Id }))
      };

      const response = await MainService.ExchangeDeliveryNote(exchangeData);
      if (response === 'Success' || (response && response.Status === 'Success')) {
        const count = ordersToMove.length;
        setSuccess(`${count} order${count > 1 ? 's' : ''} exchanged successfully`);
        await reloadOrders();
        // Clear selections
        if (dragSource === 1) {
          setSelectedOrdersDN1(new Set());
        } else {
          setSelectedOrdersDN2(new Set());
        }
      } else {
        setError(response || response.Status || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error exchanging order:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
      setDraggedOrder(null);
      setDraggedOrders([]);
      setDragSource(null);
      setDragOverTarget(null);
    }
  };

  // Reload orders after exchange
  const reloadOrders = async () => {
    try {
      setLoading(true);
      if (deliveryNote1 && deliveryNote1.Id) {
        const response1 = await MainService.GetDeliveryNoteById(deliveryNote1.Id);
        setDeliveryNote1(response1);
      }
      if (deliveryNote2 && deliveryNote2.Id) {
        const response2 = await MainService.GetDeliveryNoteById(deliveryNote2.Id);
        setDeliveryNote2(response2);
      }
    } catch (err) {
      console.error('Error reloading orders:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Print delivery note
  const handlePrint = async (noteId) => {
    try {
      setLoading(true);
      const response = await MainService.PrintDeliveryNoteAllocation(noteId);
      if (response && response != null) {
        const printUrl = `${getApiBaseUrl()}ReportFiles/${response}`;
        window.open(printUrl, '_blank');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Error printing delivery note:', err);
      setError('Print Error');
    } finally {
      setLoading(false);
    }
  };

  // Initialize
  const init = () => {
    setDeliveryNote1(null);
    setDeliveryNote2(null);
    setFilter1Text('');
    setFilter2Text('');
    setSelectedOrdersDN1(new Set());
    setSelectedOrdersDN2(new Set());
  };

  // Filter orders
  const getFilteredOrders = (details, filterText) => {
    if (!details) return [];
    if (!filterText) return details;
    const lowerFilter = filterText.toLowerCase();
    return details.filter(detail => {
      const order = detail.Order || {};
      const voucherNo = (order.VoucherNo || '').toLowerCase();
      const clientName = ((order.Client || {}).Name || '').toLowerCase();
      const customerName = ((order.Customer || {}).Name || '').toLowerCase();
      const locationName = ((order.CustomerAddress || {}).Location || {}).Name || '';
      const emirateName = ((order.CustomerAddress || {}).Emirate || {}).Name || '';
      return voucherNo.includes(lowerFilter) ||
        clientName.includes(lowerFilter) ||
        customerName.includes(lowerFilter) ||
        locationName.toLowerCase().includes(lowerFilter) ||
        emirateName.toLowerCase().includes(lowerFilter);
    });
  };

  // Filter delivery notes for modal
  const filteredDeliveryNotes = deliveryNotes.filter(note => {
    if (!searchKeyword) return true;
    const lowerKeyword = searchKeyword.toLowerCase();
    return (note.RefNo || '').toLowerCase().includes(lowerKeyword) ||
      ((note.Driver || {}).Name || '').toLowerCase().includes(lowerKeyword);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getAddressString = (address) => {
    if (!address || !address.Location) return '-';
    const parts = [
      address.Location.Name,
      address.Emirate?.Name
    ].filter(Boolean);
    return parts.join(', ') || '-';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'deliveryExchange')}</h1>
            <p className="text-sm text-gray-500 mt-1">Exchange orders between delivery notes</p>
          </div>
          <button
            onClick={init}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Content - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Note 1 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Delivery Note 1</h2>
            <button
              onClick={() => openSelectModal(1)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Select Delivery Note 1
            </button>
          </div>

          {deliveryNote1 ? (
            <>
              {/* Delivery Note Info */}
              <div className="bg-white rounded-lg p-5 mb-5 border border-gray-200">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Ref No</span>
                      <p className="text-sm font-semibold text-gray-900">{deliveryNote1.RefNo || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Date</span>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(deliveryNote1.Date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Driver</span>
                      <p className="text-sm font-semibold text-gray-900">{(deliveryNote1.Driver || {}).Name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
                      <p className="text-sm font-semibold text-gray-900">{deliveryNote1.Status || '-'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <span className="text-xs text-gray-600">
                    {deliveryNote1.DeliveryNoteDetails?.length || 0} orders
                  </span>
                  <button
                    onClick={() => handlePrint(deliveryNote1.Id)}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                </div>
              </div>

              {/* Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Keyword"
                    value={filter1Text}
                    onChange={(e) => setFilter1Text(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Orders Cards */}
              <div
                onDragOver={(e) => handleDragOver(e, 1)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 1)}
                className={`min-h-[400px] p-4 rounded-lg transition-colors ${
                  dragOverTarget === 1 && dragSource === 2
                    ? 'bg-blue-50 border-2 border-primary border-dashed'
                    : 'bg-gray-50 border-2 border-transparent'
                }`}
                style={{ maxHeight: '500px', overflowY: 'auto' }}
              >
                {getFilteredOrders(deliveryNote1.DeliveryNoteDetails, filter1Text).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No orders found</p>
                    {dragOverTarget === 1 && dragSource === 2 && (
                      <p className="text-sm text-primary mt-2">Drop here to exchange</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {getFilteredOrders(deliveryNote1.DeliveryNoteDetails, filter1Text).map((detail, index) => {
                      const isSelected = selectedOrdersDN1.has(detail.Id);
                      return (
                      <div
                        key={detail.Id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, detail, 1)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => toggleOrderSelection(detail.Id, 1, e)}
                        className={`bg-white border-2 rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-move ${
                          isSelected 
                            ? 'border-primary bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleOrderSelection(detail.Id, 1, { ctrlKey: true });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                            />
                            <GripVertical className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-900">{(detail.Order || {}).VoucherNo || '-'}</span>
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {(detail.Status || {}).Name || '-'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div>
                                <span className="font-medium">Delivery Date:</span> {formatDate((detail.Order || {}).DeliveryDate)}
                              </div>
                              <div>
                                <span className="font-medium">Client:</span> {((detail.Order || {}).Client || {}).Name || '-'}
                              </div>
                              <div>
                                <span className="font-medium">Customer:</span> {((detail.Order || {}).Customer || {}).Name || '-'}
                              </div>
                              <div className="truncate" title={getAddressString((detail.Order || {}).CustomerAddress)}>
                                <span className="font-medium">Address:</span> {getAddressString((detail.Order || {}).CustomerAddress)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No delivery note selected</p>
            </div>
          )}
        </div>

        {/* Delivery Note 2 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Delivery Note 2</h2>
            <button
              onClick={() => openSelectModal(2)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Select Delivery Note 2
            </button>
          </div>

          {deliveryNote2 ? (
            <>
              {/* Delivery Note Info */}
              <div className="bg-white rounded-lg p-5 mb-5 border border-gray-200">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Ref No</span>
                      <p className="text-sm font-semibold text-gray-900">{deliveryNote2.RefNo || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Date</span>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(deliveryNote2.Date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Driver</span>
                      <p className="text-sm font-semibold text-gray-900">{(deliveryNote2.Driver || {}).Name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
                      <p className="text-sm font-semibold text-gray-900">{deliveryNote2.Status || '-'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <span className="text-xs text-gray-600">
                    {deliveryNote2.DeliveryNoteDetails?.length || 0} orders
                  </span>
                  <button
                    onClick={() => handlePrint(deliveryNote2.Id)}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                </div>
              </div>

              {/* Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Keyword"
                    value={filter2Text}
                    onChange={(e) => setFilter2Text(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Orders Cards */}
              <div
                onDragOver={(e) => handleDragOver(e, 2)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 2)}
                className={`min-h-[400px] p-4 rounded-lg transition-colors ${
                  dragOverTarget === 2 && dragSource === 1
                    ? 'bg-purple-50 border-2 border-primary border-dashed'
                    : 'bg-gray-50 border-2 border-transparent'
                }`}
                style={{ maxHeight: '500px', overflowY: 'auto' }}
              >
                {getFilteredOrders(deliveryNote2.DeliveryNoteDetails, filter2Text).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No orders found</p>
                    {dragOverTarget === 2 && dragSource === 1 && (
                      <p className="text-sm text-primary mt-2">Drop here to exchange</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {getFilteredOrders(deliveryNote2.DeliveryNoteDetails, filter2Text).map((detail, index) => {
                      const isSelected = selectedOrdersDN2.has(detail.Id);
                      return (
                      <div
                        key={detail.Id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, detail, 2)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => toggleOrderSelection(detail.Id, 2, e)}
                        className={`bg-white border-2 rounded-lg p-4 shadow-sm hover:shadow-md transition-all cursor-move ${
                          isSelected 
                            ? 'border-primary bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleOrderSelection(detail.Id, 2, { ctrlKey: true });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                            />
                            <GripVertical className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-gray-900">{(detail.Order || {}).VoucherNo || '-'}</span>
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                {(detail.Status || {}).Name || '-'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                              <div>
                                <span className="font-medium">Delivery Date:</span> {formatDate((detail.Order || {}).DeliveryDate)}
                              </div>
                              <div>
                                <span className="font-medium">Client:</span> {((detail.Order || {}).Client || {}).Name || '-'}
                              </div>
                              <div>
                                <span className="font-medium">Customer:</span> {((detail.Order || {}).Customer || {}).Name || '-'}
                              </div>
                              <div className="truncate" title={getAddressString((detail.Order || {}).CustomerAddress)}>
                                <span className="font-medium">Address:</span> {getAddressString((detail.Order || {}).CustomerAddress)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>No delivery note selected</p>
            </div>
          )}
        </div>
      </div>

      {/* Refresh Button */}
      {deliveryNote1 && deliveryNote2 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center">
            <button
              onClick={reloadOrders}
              disabled={loading}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            💡 Hold Ctrl and click to select multiple orders, then drag to exchange them
          </p>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <span className="text-gray-700 font-medium">Loading...</span>
          </div>
        </div>,
        document.body
      )}

      {/* Select Delivery Note Modal 1 */}
      {showSelectModal1 && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Select Delivery Note 1</h2>
                <button
                  onClick={() => setShowSelectModal1(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No or Driver..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredDeliveryNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No delivery notes found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDeliveryNotes.map((note) => (
                    <div
                      key={note.Id}
                      onClick={() => selectDeliveryNote(note, 1)}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">Ref No: {note.RefNo || '-'}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Date: {formatDate(note.Date)} | Driver: {(note.Driver || {}).Name || '-'} | Status: {note.Status || '-'}
                          </p>
                        </div>
                        <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Select Delivery Note Modal 2 */}
      {showSelectModal2 && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Select Delivery Note 2</h2>
                <button
                  onClick={() => setShowSelectModal2(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No or Driver..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredDeliveryNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No delivery notes found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDeliveryNotes.map((note) => (
                    <div
                      key={note.Id}
                      onClick={() => selectDeliveryNote(note, 2)}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">Ref No: {note.RefNo || '-'}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Date: {formatDate(note.Date)} | Driver: {(note.Driver || {}).Name || '-'} | Status: {note.Status || '-'}
                          </p>
                        </div>
                        <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeliveryExchange;

